// ════════════════════════════════════════════════════════════════════
// TERROR — THE N-SEAT LAYER
// ────────────────────────────────────────────────────────────────────
// Pure functions only — no Firebase, no React. Every helper here takes
// a match snapshot and returns an answer; Firebase writes derived from
// those answers happen in gameManager.js, the game's one Firebase
// touchpoint (Shatterfield.js has never called Firebase directly, and
// this file keeps that invariant true).
//
// WHY THIS EXISTS: Shatterfield.js was built assuming exactly two
// seats — every "opponent" was `players[playerIdx===0?1:0]`, hardcoded
// at three chokepoints (the main component, the Tome/Curio effect
// dispatcher's lexical shadow, and ProphecyButton). Rather than rewire
// the ~9,200-line duel component's internals for 2v2/FFA, this module
// gives those three chokepoints one new resolved value each — a target
// seat, a next-turn seat, an effective anima — and everything
// downstream (attack resolution, curios, response windows) keeps
// working exactly as it did, because none of it ever knew the seat
// index was hardcoded; it just used the variable the chokepoint handed
// it.
//
// THE ONE INVARIANT THAT MATTERS: every function below, called on a
// classic 2-seat match with no teams, returns EXACTLY what the old
// inline `X === 0 ? 1 : 0` ternary returned. 1v1 is untouched by this
// file's existence — it only starts doing new things once a match
// actually has 3+ seats or a `teams` map.
// ════════════════════════════════════════════════════════════════════

// ── SEAT BASICS ─────────────────────────────────────────────────────

export const seatCountOf = (match) => match?.seatCount || 2;

export const allSeatIdxs = (match) => {
  const n = seatCountOf(match);
  return Array.from({ length: n }, (_, i) => i);
};

// An empty (unfilled) seat can't be targeted or take a turn, so it
// counts as "eliminated" for rotation/targeting purposes without ever
// needing its own anima to say so.
export const isEliminated = (match, idx) => {
  const p = match?.players?.[idx];
  if (!p) return true;
  if (p.eliminated) return true;
  return getEffectiveAnima(match, idx) <= 0;
};

export const livingSeats = (match) =>
  allSeatIdxs(match).filter((i) => match?.players?.[i] && !isEliminated(match, i));

// ── TEAMS ───────────────────────────────────────────────────────────
// match.teams: { [seatIdx]: teamId } — absent/null entry = no team
// (Free-For-All). match.sharedAnimaTeams: bool — whether teamed seats
// pool one Anima total (2v2, per caP'n: "they share Anima and not
// Khao"). Khao, hand, field, grimoire always stay per-seat — only the
// life number is ever shared.

export const teamOf = (match, idx) => {
  const t = match?.teams?.[idx];
  return t === undefined ? null : t;
};

export const teammatesOf = (match, idx) => {
  const t = teamOf(match, idx);
  if (t == null) return [];
  return allSeatIdxs(match).filter((i) => i !== idx && teamOf(match, i) === t);
};

// Every seat that's a legal attack target for `idx` — in FFA that's
// everyone else still seated ("attack whoever you want, gang up on one
// player, it's like 1v1 either way" — caP'n); in a teamed mode it's
// only the other team's seats, never an ally.
export const enemySeatsOf = (match, idx) => {
  const t = teamOf(match, idx);
  return allSeatIdxs(match).filter((i) => {
    if (i === idx) return false;
    if (!match?.players?.[i]) return false;
    if (t == null) return true;
    return teamOf(match, i) !== t;
  });
};

export const livingEnemySeatsOf = (match, idx) =>
  enemySeatsOf(match, idx).filter((i) => !isEliminated(match, i));

// ── R136 · ANYONE MAY ANSWER ─────────────────────────────────────────
// caP'n, 2026-09-15: *"anyone can answer it, even in a free for all, that
// adds a lot of drama"*. A declaration opens a response window for EVERY
// living adversary of the declarer, not only the seat it was aimed at.
// At 1v1 that is the one other seat, exactly as before. ⚠ ONE LEG IS
// V'AEL'S: "anyone" is read as every living ADVERSARY — a 2v2 teammate
// does not answer their own partner's Tome. Reverse at this line.
export const answerersFor = (match, pa) => {
  if (!pa || pa.by == null) return [];
  const living = livingEnemySeatsOf(match, pa.by);
  if (living.length) return living;
  // A pre-N-seat pendingAction on a 2-seat board: the other chair.
  return seatCountOf(match) <= 2 ? [pa.by === 0 ? 1 : 0] : [];
};

// The declarer's read of the per-seat slots. `pendingResponses/{seat}`
// is one writer per node; the legacy single `pendingResponse` is still
// honoured as seat pa.targetSeat's answer so a client one version behind
// cannot stall a duel. `answered` is ordered by arrival (tie → lowest
// seat), because last-declared-first-resolved wants exactly one link
// on top of the declaration; the rest may rebut on the next link.
export const collectPvPResponses = (match, pa, answerers = answerersFor(match, pa)) => {
  const slots = match?.pendingResponses || {};
  const rows = [];
  for (const seat of answerers) {
    const r = slots[seat];
    if (r && r.seq === pa.seq) { rows.push({ ...r, seat }); continue; }
    const legacy = match?.pendingResponse;
    if (legacy && legacy.seq === pa.seq && (pa.targetSeat == null || pa.targetSeat === seat)) rows.push({ ...legacy, seat });
  }
  const answered = rows.filter((r) => r.responded)
    .sort((a, b) => ((a.at || 0) - (b.at || 0)) || (a.seat - b.seat));
  const allDeclined = answerers.length > 0 && answerers.every((s) => rows.some((r) => r.seat === s)) && answered.length === 0;
  return { rows, answered, allDeclined };
};

// ── ANIMA — individual, or shared per team ───────────────────────────

export const sharesAnimaWithTeam = (match, idx) => {
  const t = teamOf(match, idx);
  return t != null && !!match?.sharedAnimaTeams;
};

export const getEffectiveAnima = (match, idx) => {
  if (sharesAnimaWithTeam(match, idx)) {
    const t = teamOf(match, idx);
    const pooled = match?.teamAnima?.[t];
    if (pooled != null) return pooled;
  }
  return match?.players?.[idx]?.anima ?? 30;
};

// Overlay a seat object with its effective anima. Safe on null/
// undefined (passes it straight through) so call sites never need an
// extra guard around it.
export const withEffectiveAnima = (match, seatObj, idx) => {
  if (!seatObj) return seatObj;
  const eff = getEffectiveAnima(match, idx);
  return seatObj.anima === eff ? seatObj : { ...seatObj, anima: eff };
};

// ── TARGETING — "opponent" generalized to "the chosen enemy seat" ───
// 1v1 (seatCount <= 2): always the one other seat — byte-identical to
// the historical inline ternary, on purpose, so classic matches are
// untouched.
// 3-4p FFA / 2v2: the attacker's currently-declared target
// (`match.activeTarget[idx]`), defaulting to the first living enemy
// seat so nothing is ever undefined before a target-picker UI has run.

export const defaultTargetOf = (match, idx) => {
  const enemies = livingEnemySeatsOf(match, idx);
  return enemies.length ? enemies[0] : null;
};

export const getTargetIdx = (match, idx) => {
  if (seatCountOf(match) <= 2) return idx === 0 ? 1 : 0;
  const chosen = match?.activeTarget?.[idx];
  if (chosen != null && !isEliminated(match, chosen) && enemySeatsOf(match, idx).includes(chosen)) {
    return chosen;
  }
  return defaultTargetOf(match, idx);
};

// ── TURN ORDER — deliberately NOT the same question as targeting. In
// 1v1, "my opponent" and "whoever goes next" happen to be the same
// seat, which is how the old code got away with one ternary answering
// both. They stop being the same thing the moment there are 3+ seats:
// I can attack seat C while seat B goes next. Every place in
// Shatterfield.js that was really asking "who plays after me" (turn-end
// cleanup, Fallen rising, Khao refresh) asks THIS, never getTargetIdx.
export const getNextActiveSeat = (match, fromIdx) => {
  const order = Array.isArray(match?.turnOrder) && match.turnOrder.length
    ? match.turnOrder
    : allSeatIdxs(match);
  const living = livingSeats(match);
  if (!living.length) return fromIdx;
  const pos = order.indexOf(fromIdx);
  const startAt = pos === -1 ? 0 : pos;
  for (let step = 1; step <= order.length; step++) {
    const cand = order[(startAt + step) % order.length];
    if (living.includes(cand)) return cand;
  }
  return fromIdx; // only seat left standing — match should already be over
};

// ── COIN TOSS — "who goes first, that's just classic coin toss, keep
// tossing till there's winners and losers" (caP'n). A full seat order
// built one coin flip at a time (repeated flips resolve each position,
// same as the existing 1v1 flip) instead of reaching for a different
// kind of randomness once there are more than two seats.
//
// Deterministic from `seed` (createdAt works, same source the existing
// 1v1 flip already uses) so every client in the match computes the
// identical order with no coordination — the same reasoning the 1v1
// coin flip's own comment gives for using createdAt parity.
export const decideTurnOrder = (seatIdxs, seed) => {
  const order = [...seatIdxs];
  let s = (seed || 1) >>> 0;
  const nextCoin = () => {
    s = (Math.imul(s, 1103515245) + 12345) >>> 0;
    return (s >>> 16) & 1; // one coin flip drawn off the stream
  };
  // Fisher-Yates, but every swap position is decided by drawing coins
  // one at a time and rejecting an out-of-range result — "keep
  // tossing till there's a winner" applied to each remaining slot.
  for (let i = order.length - 1; i > 0; i--) {
    let draws = 0;
    while ((1 << draws) <= i) draws++;
    let bits;
    do {
      bits = 0;
      for (let b = 0; b < draws; b++) bits = (bits << 1) | nextCoin();
    } while (bits > i);
    [order[i], order[bits]] = [order[bits], order[i]];
  }
  return order;
};

// ── WINNER — a match's `winner` field is a seat idx (1v1, FFA) or a
// team id (2v2). Read it through this, never a raw `=== playerIdx`, so
// both shapes just work everywhere the game asks "did I win."
// ⚠ R98 (2026-08-25) — IT READS `winners` FIRST NOW. A Solrend can end
// with more than one winner: caP'n's ruling is that a Prophecy
// fulfilled by somebody else's finishing blow ALSO wins, and that the
// board winner still won. `match.winner` remains the PRIMARY winner
// and every pre-R98 consumer keeps working off it unchanged; the
// `winners` array is the truth and is only present when there is more
// than one. Both shapes are asked here so no caller has to know which
// it is holding — which is the same reason this function existed in
// the first place, one ruling later.
export const didSeatWin = (match, idx) => {
  const t = teamOf(match, idx);
  const claims = Array.isArray(match?.winners) && match.winners.length
    ? match.winners
    : (match?.winner == null ? [] : [match.winner]);
  if (!claims.length) return false;
  return claims.some((w) => w === idx || (t != null && w === t));
};

// Every winner of this Solrend, as a list, whichever shape was stored.
// One element for an ordinary duel; two or more under R98.
export const winnersOfMatch = (match) => {
  if (Array.isArray(match?.winners) && match.winners.length) return [...match.winners];
  return match?.winner == null ? [] : [match.winner];
};
export const isCoWonMatch = (match) => winnersOfMatch(match).length > 1;

// ── ELIMINATION / MATCH-OVER — for 3+ seat FFA and for 2v2: knock a
// seat (or, under shared Anima, its whole team at once) out when its
// life hits 0; the match ends when one seat or team remains standing.
export const survivingTeamsOrSeats = (match) => {
  const living = livingSeats(match);
  const seen = new Set();
  const out = [];
  for (const idx of living) {
    const t = teamOf(match, idx);
    const key = t == null ? `seat:${idx}` : `team:${t}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t == null ? idx : t);
  }
  return out;
};

export const matchIsDecided = (match) => survivingTeamsOrSeats(match).length <= 1;

const matchSeats = {
  seatCountOf, allSeatIdxs, isEliminated, livingSeats,
  teamOf, teammatesOf, enemySeatsOf, livingEnemySeatsOf,
  sharesAnimaWithTeam, getEffectiveAnima, withEffectiveAnima,
  defaultTargetOf, getTargetIdx, getNextActiveSeat, decideTurnOrder,
  didSeatWin, winnersOfMatch, isCoWonMatch, survivingTeamsOrSeats, matchIsDecided,
  answerersFor, collectPvPResponses,
};
export default matchSeats;
